import filter from 'lodash/filter';
import union from 'lodash/unionBy';
import forEach from 'lodash/forEach';
import remove from 'lodash/remove';
import find from 'lodash/find';
import { Assets, uploadAsset } from 'modules/assets';
import {
  patchAgent,
  fetchAgent,
  postAgent,
  fetchAgentAssignments,
  patchAgentAssignment,
  postAgentAssignment,
  removeAgentAssignment,
  fetchAgentAssignmentsByAgentId,
} from './api';
import {
  createAsyncThunk,
  createSlice,
  createSelector,
} from '@reduxjs/toolkit';

export const loadAgent = createAsyncThunk(
  'agents/load',
  async (agentId) => await fetchAgent(agentId),
);

export function loadAgents(agentAssignments) {
  return (dispatch) => {
    forEach(agentAssignments, (agentAssignment) => {
      const { agentId } = agentAssignment;
      if (agentId) {
        return dispatch(loadAgent(agentId));
      }
    });
  };
}

export const createAgent = createAsyncThunk(
  'agents/create',
  async ({ firstName, lastName }) => await postAgent(firstName, lastName),
);

export function uploadAgentProfileImage(accountId, imageFile) {
  return (dispatch) => {
    const context = Assets.createAccountContext(accountId);
    return dispatch(
      uploadAsset({
        imageFile,
        assetType: 'image',
        assetClass: 'profileImage',
        context,
      }),
    );
  };
}

const update = createAsyncThunk(
  'agents/update',
  async ({ agentId, values }) => await patchAgent(agentId, values),
);
export function updateAgent(accountId, agentId, values) {
  return (dispatch) => {
    const { profileImage } = values;

    if (profileImage) {
      return dispatch(uploadAgentProfileImage(accountId, profileImage)).then(
        (response) => {
          const agentValues = {
            ...values,
            profileImage: response.payload,
          };

          return dispatch(update({ agentId, values: agentValues }));
        },
      );
    }

    return dispatch(update({ agentId, values }));
  };
}

function removeAgentFromLoading(loadingAgents, loadedAgent) {
  remove(loadingAgents, (loadingAgent) => {
    return loadingAgent === loadedAgent;
  });
  return loadingAgents;
}

export const loadAgentAssignments = createAsyncThunk(
  'agentAssignments/fetchAgentAssignments',
  async (_) => await fetchAgentAssignments(),
);
export const createAgentAssignment = createAsyncThunk(
  'agentAssignments/postAgentAssignment',
  async ({ talentId, values }) => await postAgentAssignment(talentId, values),
);
export const updateAgentAssignment = createAsyncThunk(
  'agentAssignments/patchAgentAssignment',
  async ({ talentId, assignmentId, values }) =>
    await patchAgentAssignment(talentId, assignmentId, values),
);
export const deleteAgentAssignment = createAsyncThunk(
  'agentAssignments/removeAgentAssignment',
  async (assignmentId) => await removeAgentAssignment(assignmentId),
);

export const loadAgentAssignmentsById = createAsyncThunk(
  'agentAssignments/fetchAgentAssignmentsById',
  async (agentId) => fetchAgentAssignmentsByAgentId(agentId),
);

const agentsSlice = createSlice({
  name: 'agents',
  initialState: {
    agentAssignments: null,
    loading: false,
    agent: null,
    agents: [],
    loadingAgents: [],
  },
  reducers: {
    clearAgentsData: (state) => {
      state.agent = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadAgent.pending, (state, action) => {
        state.loading = true;
        state.loadingAgents = [...state.loadingAgents, action.meta.arg];
      })
      .addCase(loadAgent.fulfilled, (state, action) => {
        state.loading = false;
        state.agent = action.payload.data;
        state.agents = union([action.payload.data], state.agents);
        state.loadingAgents = removeAgentFromLoading(
          [...state.loadingAgents],
          action.meta.arg,
        );
      })
      .addCase(loadAgent.rejected, (state, _) => {
        state.loading = false;
      })
      .addCase(createAgent.pending, (state, _) => {
        state.loading.true;
      })
      .addCase(createAgent.fulfilled, (state, action) => {
        state.loading = false;
        state.agent = action.payload.data;
      })
      .addCase(update.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(update.fulfilled, (state, action) => {
        const updatedAgent = action.payload.data;
        state.loading = false;
        state.agent = updatedAgent;
      })
      .addCase(loadAgentAssignments.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(loadAgentAssignments.fulfilled, (state, action) => {
        state.agentAssignments = action.payload.data;
        state.loading = false;
      })
      .addCase(createAgentAssignment.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(createAgentAssignment.fulfilled, (state, action) => {
        state.loading = false;
        state.agentAssignments = [
          ...state.agentAssignments,
          action.payload.data,
        ];
      })
      .addCase(updateAgentAssignment.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(updateAgentAssignment.fulfilled, (state, action) => {
        state.loading = false;
        state.agentAssignments = state.agentAssignments.map((assignment) =>
          assignment.id !== action.payload.data.id
            ? assignment
            : action.payload.data,
        );
      })
      .addCase(deleteAgentAssignment.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(deleteAgentAssignment.fulfilled, (state, action) => {
        state.loading = false;
        state.agentAssignments = filter(
          state.agentAssignments,
          (assignment) => assignment.id !== action.meta.arg,
        );
      })
      .addCase(loadAgentAssignmentsById.pending, (state, _) => {
        state.loading = true;
      })
      .addCase(loadAgentAssignmentsById.fulfilled, (state, action) => {
        state.agentAssignments = action.payload.data;
        state.loading = false;
      });
  },
  selectors: {
    getAgent: (state) => state.agent,
    getAgentId: (state) => state.agent.id,
    getAgentAssignments: (state) => state.agentAssignments,
    getAgentsLoading: (state) => state.loading,
    getAgents: (state) => state.agents,
    getLoadingAgents: (state) => state.loadingAgents,
  },
});
export const reducer = agentsSlice.reducer;
export const { clearAgentsData } = agentsSlice.actions;
export const {
  getAgent,
  getAgentId,
  getAgentAssignments,
  getAgentsLoading,
  getAgents,
  getLoadingAgents,
} = agentsSlice.selectors;

export const getAgentById = (agentId) =>
  createSelector(getAgents, (agents) => find(agents, { id: agentId }));
